/**
 * /telefoon — power-user page for call history + voicemails + phone settings.
 *
 * Four tabs:
 *   - Gesprekken: CallHistoryList + CallDetailPanel (filters, detail drill-down)
 *   - Opvolgen: TakenList (opvolg-werkvoorraad uit /api/tasks — ALLEEN gemist +
 *     voicemail via ?kind=telefoon, met toewijzen/due/snooze/afspelen/terugbellen
 *     + toetsenbord-triage). Systeemsignalen (convert, doorverwijs, financieel)
 *     stonden hier vroeger tussen en horen nu op /signalen.
 *   - Belredenen: BelredenenPanel (verdeling + trend + wekelijkse aanbevelingen)
 *   - Instellingen: PresenceToggle + DeviceSelector + mic test + notifications
 *
 * URL supports `?tab=settings` / `?tab=opvolgen` / `?tab=belredenen` for deep-linking.
 * `?tab=voicemails` blijft werken als alias voor `opvolgen`.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CallHistoryList } from '../components/phone/CallHistoryList';
import { TakenList } from '../components/phone/TakenList';
import { BelredenenPanel } from '../components/phone/BelredenenPanel';
import { LeerpuntenPanel } from '../components/phone/LeerpuntenPanel';
import { PresenceToggle } from '../components/phone/PresenceToggle';
import { DeviceSelector } from '../components/phone/DeviceSelector';
import { MicCheck } from '../components/phone/MicCheck';
import { NotificationPermissionBanner } from '../components/phone/NotificationPermissionBanner';
import { useCallDrawerStore } from '../stores/call-drawer-store';
import { useUserRole } from '../hooks/useUserRole';
import { supabase } from '../lib/supabase';

type Tab = 'gesprekken' | 'opvolgen' | 'belredenen' | 'leerpunten' | 'instellingen';

function tabFromParam(param: string | null): Tab {
  if (param === 'settings') return 'instellingen';
  // `voicemails` blijft als alias werken voor bestaande deep-links/bookmarks.
  if (param === 'opvolgen' || param === 'voicemails') return 'opvolgen';
  if (param === 'belredenen') return 'belredenen';
  if (param === 'leerpunten') return 'leerpunten';
  return 'gesprekken';
}

export function TelefoonPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => tabFromParam(searchParams.get('tab')));
  const [micCheckOpen, setMicCheckOpen] = useState(false);
  const [openTasks, setOpenTasks] = useState<number | null>(null);
  const [openLeerpunten, setOpenLeerpunten] = useState<number | null>(null);
  const openCallId = useCallDrawerStore((s) => s.openCallId);
  const openCall = useCallDrawerStore((s) => s.open);
  const closeCall = useCallDrawerStore((s) => s.close);
  const { role } = useUserRole();

  // Teller voor de opvolgen-tab, ook zonder dat de tab open staat.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
        // kind=telefoon: systeemsignalen (convert/doorverwijs/financieel) horen
        // op /signalen, niet in deze teller — anders jaagt callcenter op werk
        // dat hier niet staat.
        const r = await fetch('/api/tasks?status=open&limit=1&kind=telefoon', { headers });
        if (!r.ok) return;
        const json = await r.json();
        if (!cancelled) setOpenTasks(json.total ?? 0);
      } catch {
        // badge is niet kritisch
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const switchTab = useCallback((next: Tab) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'instellingen') params.set('tab', 'settings');
    else if (next === 'opvolgen') params.set('tab', 'opvolgen');
    else if (next === 'belredenen') params.set('tab', 'belredenen');
    else if (next === 'leerpunten') params.set('tab', 'leerpunten');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const tabs: Array<[Tab, string]> = [
    ['gesprekken', 'Gesprekken'],
    ['opvolgen', openTasks ? `Opvolgen (${openTasks})` : 'Opvolgen'],
    ['belredenen', 'Belredenen'],
    ['leerpunten', openLeerpunten ? `Leerpunten (${openLeerpunten})` : 'Leerpunten'],
    ['instellingen', 'Instellingen'],
  ];

  return (
    <>
      {/* AppLayout's <main> is overflow-hidden, so the page owns its scroll —
          without this the detail panel below de lange lijst wordt geclipt.
          `w-full` is niet cosmetisch: <main> is een kolom-flexbox en een
          cross-as `mx-auto` zet stretch uit, waardoor deze wrapper zonder
          definiete breedte om zijn inhoud heen krimpt. De pagina werd daarmee
          zo breed als wat er toevallig in stond — dát is de smal/breed-sprong,
          niet de split eronder. Opvolgen is een tweepaneels-werkplek en mag de
          volle breedte; de andere tabs blijven op een leesbare 1400. */}
      <div
        className={`h-full overflow-y-auto p-6 w-full mx-auto ${
          tab === 'opvolgen' ? 'max-w-none' : 'max-w-[1400px]'
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-heading font-semibold text-main">Telefoon</h1>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6 flex gap-1">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => switchTab(id)}
              aria-selected={tab === id}
              role="tab"
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id
                  ? 'border-indigo-500 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'gesprekken' && (
          <div>
            <CallHistoryList
              selectedCallId={openCallId}
              onSelect={(id) => (id === openCallId ? closeCall() : openCall(id))}
            />
          </div>
        )}

        {tab === 'opvolgen' && (
          <div>
            <TakenList onCountChange={setOpenTasks} />
          </div>
        )}

        {tab === 'belredenen' && <BelredenenPanel />}

        {tab === 'leerpunten' && <LeerpuntenPanel onCountChange={setOpenLeerpunten} />}

        {tab === 'instellingen' && (
          <div className="max-w-2xl space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-base font-semibold text-slate-800 mb-3">Beschikbaarheid</h2>
              <PresenceToggle />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-base font-semibold text-slate-800 mb-3">Audio-apparaten</h2>
              <DeviceSelector />
              <div className="mt-4 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setMicCheckOpen((v) => !v)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                >
                  {micCheckOpen ? 'Microfoon check sluiten' : 'Microfoon check uitvoeren'}
                </button>
                {micCheckOpen && (
                  <div className="mt-3">
                    <MicCheck
                      onComplete={() => setMicCheckOpen(false)}
                      onSkip={() => setMicCheckOpen(false)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-base font-semibold text-slate-800 mb-3">Browser notificaties</h2>
              <p className="text-sm text-slate-600 mb-3">
                Sta notificaties toe zodat je inkomende gesprekken hoort, ook als deze tab
                op de achtergrond staat.
              </p>
              <NotificationPermissionBanner />
            </div>

            {role === 'admin' && (
              <p className="text-sm text-slate-500">
                Routering en openingstijden beheer je in{' '}
                <Link to="/telefoon-instellingen" className="text-indigo-600 hover:text-indigo-800 underline">
                  Telefoon-instellingen
                </Link>
                .
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
